import { useEffect, useMemo } from "react";

import { TestConnectionResult } from "@/api/client";
import { getErrorMessage } from "@/api/helpers";
import { useTestExistingSource, useTestNewSource } from "@/api/queries/sources";
import TestConnectionCard from "@/components/TestConnectionCard";
import ExistingSource, {
  computeChangedFields,
  ExistingSourceTestPayload,
  PreparedSource,
  SourceType,
} from "@/types/sources";

type TestSourceConnectionProps = {
  key: string; // Required unique key identifier for the test connection target
  beforeSubmitTest?: () => boolean;
  preparedSource?: PreparedSource;
  existingSource?: ExistingSource | null;
  onTestStateChange?: (state: { status: string }) => void;
};

const TestSourceConnection = ({
  beforeSubmitTest = () => true,
  preparedSource,
  existingSource,
  onTestStateChange,
}: TestSourceConnectionProps) => {
  const testNew = useTestNewSource();
  const testExisting = useTestExistingSource();

  const isEditing = existingSource?.type === SourceType.ExistingSource;
  const activeTest = isEditing ? testExisting : testNew;

  const sourceTest: TestConnectionResult = useMemo(() => {
    if (activeTest.isPending) {
      return { status: "processing" };
    }
    if (activeTest.isError) {
      return {
        status: "error",
        message: getErrorMessage(activeTest.error),
      };
    }
    return activeTest.data || { status: undefined };
  }, [activeTest.data, activeTest.isPending, activeTest.isError, activeTest.error]);

  useEffect(() => {
    if (onTestStateChange && sourceTest.status) {
      onTestStateChange({ status: sourceTest.status });
    }
  }, [sourceTest, onTestStateChange]);

  const onTestConnection = () => {
    if (!beforeSubmitTest()) {
      return;
    }

    if (isEditing && preparedSource?.type === SourceType.PreparedSource) {
      const payload: ExistingSourceTestPayload = {
        sourceId: existingSource.id,
      };
      const changed = computeChangedFields(existingSource, preparedSource);
      console.log("changed:", changed);
      if (Object.keys(changed).length > 0) {
        payload.fields = changed;
      }
      console.log("payload:", payload);
      testExisting.mutate(payload);
    } else if (isEditing) {
      testExisting.mutate({
        sourceId: existingSource.id,
      });
    } else if (preparedSource) {
      testNew.mutate(preparedSource);
    }
  };

  return (
    <TestConnectionCard
      test={sourceTest}
      request={{ status: undefined }}
      connectionType="source"
      usesPreparedConnection={preparedSource !== undefined}
      onTestConnection={onTestConnection}
    />
  );
};

export default TestSourceConnection;
